/**
 * NotificationList Component
 *
 * Displays a list of notifications with filtering and bulk actions.
 * Includes loading, empty, and error states.
 *
 * @module NotificationList
 * @created 2025-12-19 - TASK-030
 */

import { memo, useCallback } from 'react';
import { useNotifications } from '@/hooks/use-notifications';
import { NotificationItem } from './NotificationItem';
import type { Notification, NotificationType, NotificationFilter } from '@/types/notification';
import { NOTIFICATION_TYPE_CONFIGS } from '@/types/notification';

// ========================================
// Types
// ========================================

export interface NotificationListProps {
  /** Maximum notifications to show */
  maxCount?: number;
  /** Show filters */
  showFilters?: boolean;
  /** Show bulk actions */
  showBulkActions?: boolean;
  /** Compact mode for sidebar */
  compact?: boolean;
  /** Custom class name */
  className?: string;
  /** On notification click */
  onNotificationClick?: (notification: Notification) => void;
}

// ========================================
// Filter Bar Component
// ========================================

interface FilterBarProps {
  filter: NotificationFilter;
  onFilterChange: (filter: NotificationFilter) => void;
}

function FilterBar({ filter, onFilterChange }: FilterBarProps) {
  const typeOptions: { value: NotificationType | 'all'; label: string }[] = [
    { value: 'all', label: '전체' },
    ...Object.values(NOTIFICATION_TYPE_CONFIGS).map((config) => ({
      value: config.type,
      label: config.label,
    })),
  ];

  const readOptions = [
    { value: 'all', label: '전체' },
    { value: false, label: '읽지 않음' },
    { value: true, label: '읽음' },
  ];

  return (
    <div className="flex flex-wrap gap-3 mb-4">
      {/* Type filter */}
      <select
        value={filter.type || 'all'}
        onChange={(e) =>
          onFilterChange({
            ...filter,
            type: e.target.value as NotificationType | 'all',
          })
        }
        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        {typeOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      {/* Read filter */}
      <select
        value={filter.read === undefined ? 'all' : String(filter.read)}
        onChange={(e) => {
          const value = e.target.value;
          onFilterChange({
            ...filter,
            read: value === 'all' ? 'all' : value === 'true',
          });
        }}
        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        {readOptions.map((opt) => (
          <option key={String(opt.value)} value={String(opt.value)}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

// ========================================
// Loading Skeleton
// ========================================

function LoadingSkeleton() {
  return (
    <div className="space-y-4 animate-pulse" data-testid="notification-list-loading">
      {[1, 2, 3].map((i) => (
        <div key={i} className="p-4 border border-gray-200 rounded-lg">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-gray-200" />
            <div className="flex-1">
              <div className="h-4 bg-gray-200 rounded w-20 mb-2" />
              <div className="h-5 bg-gray-200 rounded w-48 mb-2" />
              <div className="h-4 bg-gray-200 rounded w-full mb-2" />
              <div className="h-3 bg-gray-200 rounded w-24" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ========================================
// Empty State
// ========================================

interface EmptyStateProps {
  hasFilters: boolean;
  onClearFilters: () => void;
}

function EmptyState({ hasFilters, onClearFilters }: EmptyStateProps) {
  return (
    <div
      className="flex flex-col items-center justify-center py-12 text-gray-500"
      data-testid="notification-list-empty"
    >
      <div className="text-5xl mb-4">🔔</div>
      {hasFilters ? (
        <>
          <p className="font-medium">필터에 맞는 알림이 없습니다</p>
          <button
            onClick={onClearFilters}
            className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            필터 초기화
          </button>
        </>
      ) : (
        <>
          <p className="font-medium">알림이 없습니다</p>
          <p className="text-sm mt-1">새로운 알림이 도착하면 여기에 표시됩니다</p>
        </>
      )}
    </div>
  );
}

// ========================================
// Error State
// ========================================

function ErrorState({ error, onRetry }: { error: Error; onRetry: () => void }) {
  return (
    <div
      className="flex flex-col items-center justify-center py-12 text-red-500"
      data-testid="notification-list-error"
    >
      <div className="text-5xl mb-4">⚠️</div>
      <p className="font-medium">알림을 불러오는데 실패했습니다</p>
      <p className="text-sm mt-1 text-gray-500">{error.message}</p>
      <button
        onClick={onRetry}
        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
      >
        다시 시도
      </button>
    </div>
  );
}

// ========================================
// Main Component
// ========================================

export const NotificationList = memo(function NotificationList({
  maxCount = 50,
  showFilters = true,
  showBulkActions = true,
  compact = false,
  className = '',
  onNotificationClick,
}: NotificationListProps) {
  const {
    notifications,
    isLoading,
    error,
    unreadCount,
    filter,
    setFilter,
    markAsRead,
    markAllAsRead,
    refetch,
    isMarkingRead,
  } = useNotifications({ maxCount });

  const hasFilters = filter.type !== 'all' || filter.read !== 'all';

  const handleClearFilters = useCallback(() => {
    setFilter({ type: 'all', read: 'all' });
  }, [setFilter]);

  // Loading state
  if (isLoading) {
    return <LoadingSkeleton />;
  }

  // Error state
  if (error) {
    return <ErrorState error={error} onRetry={refetch} />;
  }

  return (
    <div className={`${className}`} data-testid="notification-list">
      {/* Header */}
      {showBulkActions && (
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-gray-900">알림</h3>
            {unreadCount > 0 && (
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                {unreadCount}
              </span>
            )}
          </div>

          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              disabled={isMarkingRead}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
            >
              {isMarkingRead ? '처리 중...' : '모두 읽음 표시'}
            </button>
          )}
        </div>
      )}

      {/* Filters */}
      {showFilters && <FilterBar filter={filter} onFilterChange={setFilter} />}

      {/* Notification List */}
      {notifications.length === 0 ? (
        <EmptyState hasFilters={hasFilters} onClearFilters={handleClearFilters} />
      ) : (
        <div className="space-y-3">
          {notifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onMarkAsRead={markAsRead}
              onClick={onNotificationClick}
              compact={compact}
            />
          ))}
        </div>
      )}

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="mt-4 text-center">
          <button
            onClick={refetch}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            새로고침
          </button>
        </div>
      )}
    </div>
  );
});

export default NotificationList;
